import React from "react";
import { AlertTriangle, Inbox, LoaderCircle } from "lucide-react";
import { colors, radius, shadows, spacing, typography } from "./tokens";

const VARIANT_MAP = {
  loading: {
    defaultTitle: "Loading",
    defaultDescription: "Please wait while Glyder prepares this workspace.",
    icon: LoaderCircle,
    borderColor: colors.accentBorder,
    background: colors.surfaceSubtle,
    iconBackground: colors.accentSubtle,
    iconBorder: colors.accentBorder,
    iconColor: colors.brandPrimary,
  },
  empty: {
    defaultTitle: "Nothing to show yet",
    defaultDescription: "This area will populate as data becomes available.",
    icon: Inbox,
    borderColor: colors.border,
    background: colors.surfaceSubtle,
    iconBackground: colors.surface,
    iconBorder: colors.border,
    iconColor: colors.textMuted,
  },
  error: {
    defaultTitle: "We could not load this section",
    defaultDescription: "Refresh and try again. If the problem continues, check your connection or permissions.",
    icon: AlertTriangle,
    borderColor: colors.dangerBorder,
    background: colors.dangerSubtle,
    iconBackground: colors.surface,
    iconBorder: colors.dangerBorder,
    iconColor: colors.danger,
  },
};

export default function PageState({
  state = "empty",
  title,
  description,
  icon,
  action,
  actions,
  compact = false,
  align = "center",
  className = "page-state",
  style,
  titleStyle,
  descriptionStyle,
  actionsStyle,
}) {
  const variant = VARIANT_MAP[state] || VARIANT_MAP.empty;
  const Icon = icon || variant.icon;
  const resolvedActions = actions ?? action ?? null;
  const isCentered = align !== "left";
  const role = state === "error" ? "alert" : "status";
  const ariaLive = state === "error" ? "assertive" : "polite";

  return (
    <div
      className={className}
      role={role}
      aria-live={ariaLive}
      style={{
        ...containerStyle,
        background: variant.background,
        borderColor: variant.borderColor,
        ...(compact ? compactContainerStyle : null),
        ...(isCentered ? centerAlignStyle : leftAlignStyle),
        ...style,
      }}
    >
      <style>{spinnerCss}</style>
      {Icon ? (
        <div
          style={{
            ...iconWrapStyle,
            width: compact ? 40 : 52,
            height: compact ? 40 : 52,
            borderColor: variant.iconBorder,
            background: variant.iconBackground,
            color: variant.iconColor,
          }}
        >
          <Icon
            size={compact ? 18 : 22}
            aria-hidden="true"
            className={state === "loading" ? "glyder-page-state__spinner" : undefined}
          />
        </div>
      ) : null}

      <div style={contentWrapStyle}>
        <div style={{ ...baseTitleStyle, ...(compact ? compactTitleStyle : null), ...titleStyle }}>
          {title || variant.defaultTitle}
        </div>
        {(description || variant.defaultDescription) ? (
          <div
            style={{
              ...baseDescriptionStyle,
              ...(compact ? compactDescriptionStyle : null),
              ...descriptionStyle,
            }}
          >
            {description || variant.defaultDescription}
          </div>
        ) : null}
      </div>

      {resolvedActions ? (
        <div
          style={{
            ...baseActionsStyle,
            ...(isCentered ? centerActionsStyle : null),
            ...actionsStyle,
          }}
        >
          {resolvedActions}
        </div>
      ) : null}
    </div>
  );
}

const spinnerCss = `
  @keyframes glyder-page-state-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  .glyder-page-state__spinner {
    animation: glyder-page-state-spin 1s linear infinite;
  }
`;

const containerStyle = {
  width: "100%",
  display: "grid",
  gap: spacing.md,
  padding: spacing.xl * 2,
  borderRadius: radius.lg,
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: colors.border,
  boxSizing: "border-box",
  boxShadow: shadows.xs,
};

const compactContainerStyle = {
  padding: spacing.xl + spacing.sm,
  gap: spacing.sm,
};

const centerAlignStyle = {
  justifyItems: "center",
  textAlign: "center",
};

const leftAlignStyle = {
  justifyItems: "start",
  textAlign: "left",
};

const iconWrapStyle = {
  borderRadius: radius.pill,
  border: `1px solid ${colors.border}`,
  display: "grid",
  placeItems: "center",
  boxShadow: shadows.xs,
};

const contentWrapStyle = {
  display: "grid",
  gap: spacing.xs,
};

const baseTitleStyle = {
  color: colors.text,
  fontSize: typography.size.sm,
  fontWeight: typography.weight.bold,
  lineHeight: typography.lineHeight.compact,
};

const compactTitleStyle = {
  fontSize: typography.size.xs,
};

const baseDescriptionStyle = {
  color: colors.textMuted,
  fontSize: typography.size.xs,
  lineHeight: typography.lineHeight.reading,
  maxWidth: 560,
};

const compactDescriptionStyle = {
  maxWidth: "none",
};

const baseActionsStyle = {
  display: "flex",
  gap: spacing.sm,
  flexWrap: "wrap",
  alignItems: "center",
};

const centerActionsStyle = {
  justifyContent: "center",
};
